/**
 * 账本与退款的**共享类型与两条派生判据**（`04-api-contract.md` §13、§13.8 指定的单一出处）。
 *
 * 为什么判据放在 `packages/types` 而不是 `apps/api`：服务端（`GET /ledger/orders` 的
 * `refundStatus`、CSV 的「退款状态」列、退款响应）与前端账本表格读的是**同一份**实现。
 * 契约 §13.8 明写禁止第二份 —— `refundedQty === quantity − shortQty` / `>=` / 漏掉某一行
 * 这几种写法在正常数据下**完全等价**，只有异常数据（历史超退、直接改库）才区分得开，
 * 而那正是最难靠 review 发现的一类分裂（与 `order.ts` 的 `isLineCovered` 同一条理由）。
 *
 * **为什么判据必须拿 `shortQty`**（Phase 6 冻结，控制器裁决 A27）：某行的**可退量** =
 * `quantity − shortQty − refundedQty`（契约 §13.6「可退量的定义」）。缺货单位在
 * `POST /orders/:id/short` 时**已经还回可售池**、并从 `Order.totalAmount` 里**减掉了**，
 * 顾客**从未为它们付钱** —— 把它们算进可退量就是**多退钱**：
 * `quantity 3 / shortQty 1 / unitPrice 45.00` 的单实收 `90.00`，按 `quantity` 退却会退 `135.00`。
 * 因此「这一行退完了」是 `refundedQty === quantity − shortQty`，**不是** `=== quantity`；
 * 写成后者还会让**含缺货行的订单永远到不了 `REFUNDED`**（`refundedQty` 被封顶在可退量上），
 * 界面与报表一直显示「部分退款」。
 *
 * ⚠️ 退款的**类型**不在这里重写一遍：`settlement.ts:7-8` 已有 `REFUND_TYPES = ['FULL', 'PARTIAL']`
 * 与 `RefundType`，本文件按控制器裁定 **A20** 直接 `import type` 复用它（下方 `RefundRecord.type` /
 * `OrderRefundResponse.type`）。本文件**也不**再定义第二份 `REFUND_TYPES`：
 * 同义字面量写两遍就与「退款类型只有一处实现」相悖，和上面那条判据是同一条纪律。
 * 同理，下面的 `OrderStatus` / `OrderChannel` / `OrderLineSource` 也从 `order.ts` `import type`
 * 复用，而不是把 `'PRODUCT' | 'PROMOTION'`、`string` 这类字面量/宽类型再写一遍
 * （`order.ts` **不**反向 import 本文件，没有循环）。
 */
/** 订单级退款状态（契约 §13.8）。字面量与 `04-api-contract.md` §13.1 逐字一致。 */
export const REFUND_STATUSES = ['NONE', 'PARTIALLY_REFUNDED', 'REFUNDED'];
/** CSV「退款状态」列的三值（**行级**）。 */
export const REFUND_LINE_LABELS = {
    NONE: '未退款',
    PARTIAL: '部分退款',
    FULL: '已全额退款',
};
/** CSV「类型」列的两值。 */
export const LINE_SOURCE_LABELS = { PRODUCT: '单品', PROMOTION: '促销方案' };
/** CSV 表头（11 列，顺序即契约 §13.5；**唯一实现**，服务端与判据都从这里取）。 */
export const LEDGER_CSV_HEADER = [
    '订单号',
    '时间',
    '商品名称',
    '货架',
    '数量',
    '单价',
    '原价',
    '小计',
    '退款状态',
    '订单备注',
    '类型',
];
/**
 * 订单级判据（契约 §13.8）：Σ refundedQty == 0 ⇒ NONE；
 * **每一行** refundedQty == quantity − shortQty（该行可退量已退光）⇒ REFUNDED；
 * 否则 PARTIALLY_REFUNDED。
 *
 * ⚠️ 脏数据下 `shortQty > quantity` ⇒ `quantity − shortQty < 0`，该行**永远不算退完**：
 * `refundedQty >= 0 > 负数`，`===` 不可能成立。这是**刻意**的 —— 直接改库/历史数据造成的
 * 负数可退量不该让整单被判成「已全额退款」（那会让前端关掉退款入口、报表把没退的钱记成已退）。
 */
export function deriveRefundStatus(lines) {
    let refundedTotal = 0;
    let everyLineFull = true;
    for (const line of lines) {
        refundedTotal += line.refundedQty;
        // `!==` 而不是 `>=`：超退行（refundedQty > 可退量）**不算**已退完。
        // 契约 §13.8 的原文是「每一行 refundedQty == quantity − shortQty」，`>=` 会把它误判成 REFUNDED。
        if (line.refundedQty !== line.quantity - line.shortQty)
            everyLineFull = false;
    }
    // 空行集合走这一支（Σ 空和是 0）⇒ NONE。空单不是「已退款」。
    if (refundedTotal === 0)
        return 'NONE';
    return everyLineFull ? 'REFUNDED' : 'PARTIALLY_REFUNDED';
}
/** 行级文案：0 ⇒ 未退款；== quantity − shortQty（可退量）⇒ 已全额退款；否则 部分退款。 */
export function refundLineStatusLabel(refundedQty, quantity, shortQty) {
    // 分支顺序承重：`(0, 0)` 与「整行缺货」（`refundedQty === 0 === quantity − shortQty`）两条
    // 都同时满足前两个条件。先判 0 才与订单级判据同源（`deriveRefundStatus` 先判 Σ==0 ⇒ NONE），
    // 也才与契约 §13.8 的字面顺序一致（`0 ⇒ 未退款` 写在最前）。写反了只在退化数据上分叉：
    // `quantity === shortQty`（可退量为 0、一分钱没退）的行会被标成「已全额退款」。
    if (refundedQty === 0)
        return REFUND_LINE_LABELS.NONE;
    if (refundedQty === quantity - shortQty)
        return REFUND_LINE_LABELS.FULL;
    return REFUND_LINE_LABELS.PARTIAL;
}
//# sourceMappingURL=ledger.js.map